/**
 * SuperMemo 2 (SM-2) Spaced Repetition Algorithm
 * https://www.supermemo.com/en/archives1990-2015/english/ol/sm2
 */

export type ReviewRating = "again" | "hard" | "good" | "easy";

export interface SM2Fields {
  ef: number;
  interval: number;
  repetitions: number;
  due_at: string;
}

/**
 * Maps rating to quality score (q) used in SM-2
 * again = 1 (complete failure)
 * hard = 3 (correct with difficulty)
 * good = 4 (correct with hesitation)
 * easy = 5 (perfect response)
 */
function ratingToQuality(rating: ReviewRating): number {
  switch (rating) {
    case "again":
      return 1;
    case "hard":
      return 3;
    case "good":
      return 4;
    case "easy":
      return 5;
  }
}

/**
 * Calculate the next review interval and ease factor using SM-2 algorithm
 */
export function updateSM2(
  rating: ReviewRating,
  current: SM2Fields
): SM2Fields {
  const q = ratingToQuality(rating);
  let { ef, interval, repetitions } = current;

  if (q >= 3) {
    // Correct response
    repetitions += 1;

    if (repetitions === 1) {
      interval = 1; // First review: 1 day
    } else if (repetitions === 2) {
      interval = 6; // Second review: 6 days
    } else {
      interval = Math.round(interval * ef);
    }
  } else {
    // Incorrect response - reset
    repetitions = 0;
    interval = 1;
  }

  // Update ease factor
  ef = ef + (0.1 - (5 - q) * (0.08 + (5 - q) * 0.02));
  
  // EF minimum is 1.3
  if (ef < 1.3) ef = 1.3;

  // Calculate next due date
  const dueDate = new Date();
  dueDate.setDate(dueDate.getDate() + interval);

  return {
    ef: Math.round(ef * 100) / 100, // Round to 2 decimal places
    interval,
    repetitions,
    due_at: dueDate.toISOString(),
  };
}

/**
 * Get a human-readable description of when the card will be due
 */
export function getNextReviewLabel(rating: ReviewRating, current: SM2Fields): string {
  const updated = updateSM2(rating, current);
  const days = updated.interval;
  
  if (days < 1) return "1 minute";
  if (days === 1) {
    // Show minutes for first-time reviews based on rating
    if (rating === "again") return "1 minute";
    if (rating === "hard") return "8 minutes";
    if (rating === "good") return "15 minutes";
    return "1 day";
  }
  if (days < 7) return `${days} days`;
  if (days < 30) return `${Math.round(days / 7)} weeks`;
  return `${Math.round(days / 30)} months`;
}
